/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import { shareReplay, tap } from 'rxjs/operators';
import { Injectable, Inject } from '@angular/core';
import { AuthApi } from '../custom';
import { User } from '../../../lib/wwg.classes';
import * as moment from 'moment';
import { OktaAuth } from '@okta/okta-auth-js';
import { OKTA_CONFIG } from '@okta/okta-angular';
import { NotificationService } from '../../../services/notification.service';
import { Router } from '@angular/router';
import { from } from 'rxjs/internal/observable/from';

type ProvisionAccountParams = { firstName?: string; surname?: string; email: string; };

@Injectable()
export class AuthService {
  private user: User | undefined;
  private oktaAuth: OktaAuth;

  constructor(
    private authApi: AuthApi,
    private notification: NotificationService,
    private router: Router,
    @Inject(OKTA_CONFIG) config: { oktaAuth: OktaAuth },
  ) {
    this.oktaAuth = config.oktaAuth;
  }

  public provisionAccount(userClaims: ProvisionAccountParams) {
    return this.authApi.provision(userClaims).pipe(
      tap(authResult => {
        this.setSession(authResult);
      })
    )
  }

  async loginRedirect() {
    if (await this.isLoggedIn()) {
      return this.router.navigate(['/setup']);
    }

    try {
      await this.oktaAuth.signInWithRedirect({ originalUri: '/' });
    } catch (err) {
      this.notification.errorHandler(err);
      console.error(err);
    }
  }

  public async getUser() {
    return this.oktaAuth.getUser();
  }

  public async getCurrentUser() {
    if (!this.user) {
      const response = await this.authApi.getCurrentUser().toPromise();
      this.user = response.data;
    }
    return this.user;
  }

  public async isSuperUser() {
    const user = await this.getCurrentUser();
    return user.isSuperUser;
  }

  private setSession(authResult) {
    const expiresAt = moment().add(authResult.expiry, 'second');

    localStorage.setItem('userId', authResult.userId);
    localStorage.setItem('token', authResult.token);
    localStorage.setItem('expiry', expiresAt.valueOf().toString());
  }

  public logout() {
    localStorage.clear();
    return from(this.oktaAuth.signOut())
  }

  public async isLoggedIn() {
    return this.oktaAuth.isAuthenticated().catch(e => {
      this.notification.errorHandler(e);
      return false;
    });
  }

  public getUserId() {
    return localStorage.getItem('userId');
  }

  public getExpiration() {
    const expiration = localStorage.getItem('expiry');
    return moment(Number(expiration));
  }
}
